import { Tab } from '@headlessui/react';
import H3 from '@sdk/components/ui/typography/H3';
import Text12 from '@sdk/components/ui/typography/Text12';
import gettext from '@sdk/lang/gettext';
import noop from '@sdk/utils/noop';
import ModalErrorDisplay from '@sdk/components/ui/modal/components/modalErrorDisplay/ModalErrorDisplay';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import type { UpdateWages } from '@web/onboarding/Checklist/onboardingChecklist.types';
import type {
  RestangularPosition,
  RestangularEmployee,
} from '@web/onboarding/Checklist/DefineWagesModal/defineWagesModal.types';
import { CenterContentRow, Container } from '@web/onboarding/Checklist/components/modalBase.style';
import {
  TabButton,
  TabButtonContent,
  TabArea,
} from '@web/onboarding/Checklist/DefineWagesModal/defineWagesModal.styles';
import WagesForEmployeesContent from '@web/onboarding/Checklist/DefineWagesModal/WagesForEmployeesContent';
import WagesForPositionsContent from '@web/onboarding/Checklist/DefineWagesModal/WagesForPositionsContent';
import useEmployeesAndPositionsTabs from '@web/onboarding/Checklist/DefineWagesModal/hooks/useEmployeesAndPositionsTabs';

export const SCROLLABLE_AREA_MAX_HEIGHT = 385;

type Props = {
  onCancel: () => void;
  afterSubmitCallback?: () => void;
  positions: RestangularPosition[];
  employees: RestangularEmployee[];
  updateWages: UpdateWages;
};

const WagesForEmployeesAndPositionsContent = ({
  employees,
  positions,
  onCancel,
  afterSubmitCallback = noop,
  updateWages,
}: Props) => {
  const {
    employeesFields,
    employeesSubmit,
    positionsFields,
    positionsSubmit,
    isEmployeesTabDirty,
    isPositionsTabDirty,
    onTabChange,
    shouldDisplayWarning,
  } = useEmployeesAndPositionsTabs({ afterSubmitCallback, employees, positions, updateWages });

  return (
    <>
      <Container pBottom={0} noMinHeight>
        <CenterContentRow pTop={20} pBottom={10}>
          <H3>{gettext('Add Wages')}</H3>
        </CenterContentRow>

        <Tab.Group onChange={onTabChange}>
          <Tab.List>
            <TabArea>
              <Tab as={TabButton}>
                {({ selected }) => (
                  <TabButtonContent
                    selected={selected}
                    selectionThemeColor={
                      isEmployeesTabDirty ? ThemeColorType.danger : ThemeColorType.primary
                    }>
                    <Text12
                      bold
                      uppercase
                      themeColor={
                        isEmployeesTabDirty
                          ? ThemeColorType.danger
                          : selected
                            ? ThemeColorType.primary
                            : ThemeColorType.gray
                      }>
                      {gettext('Employees')}
                    </Text12>
                  </TabButtonContent>
                )}
              </Tab>
              <Tab as={TabButton}>
                {({ selected }) => (
                  <TabButtonContent
                    selected={selected}
                    selectionThemeColor={
                      isPositionsTabDirty ? ThemeColorType.danger : ThemeColorType.primary
                    }>
                    <Text12
                      bold
                      uppercase
                      themeColor={
                        isPositionsTabDirty
                          ? ThemeColorType.danger
                          : selected
                            ? ThemeColorType.primary
                            : ThemeColorType.gray
                      }>
                      {gettext('Positions')}
                    </Text12>
                  </TabButtonContent>
                )}
              </Tab>
            </TabArea>
          </Tab.List>
          <Tab.Panels>
            <Tab.Panel>
              <WagesForEmployeesContent
                fields={employeesFields}
                submitWages={employeesSubmit}
                onCancel={onCancel}
              />
            </Tab.Panel>
            <Tab.Panel>
              <WagesForPositionsContent
                fields={positionsFields}
                submitWages={positionsSubmit}
                onCancel={onCancel}
              />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </Container>
      <ModalErrorDisplay
        error={
          shouldDisplayWarning
            ? gettext(
                'Psst! Wages added on this tab are not saved yet! \nRemember to save before leaving, if you still want to keep them.'
              )
            : ''
        }
      />
    </>
  );
};

export default WagesForEmployeesAndPositionsContent;
